import React from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import {
  workflowAddSigner,
  workflowAddSigningPhase,
  workflowRemoveSigner,
  workflowRemoveSigningPhase,
  workflowSetSignCoordinator,
  workflowSetSigner,
  workflowSetSignerConditionRule,
  workflowSetSignPhaseName,
  workflowSetStageConditionRule,
  workflowSortSignPhases,
  workflowToggleSkipStage,
} from '~/actions/workflow';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { TicketStageType } from '~/enums/TicketStageType';
import { getSignErrors, getSignPhases } from '~/reducers/workflow';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';

import SignStageItems from '../../shared/SignStageItems';
import SkipStageAccordion from '../../shared/SkipStageAccordion';
import WorkflowUserRoleSingleSelect from '../../shared/WorkflowUserRoleSingleSelect';
import Signer from './Signer';

function Sign({
  // connected
  conditionRule,
  coordinator,
  disabled,
  errors,
  isSkipped,
  signPhases,
  workflowAddSigner,
  workflowAddSigningPhase,
  workflowRemoveSigner,
  workflowRemoveSigningPhase,
  workflowSetSignCoordinator,
  workflowSetSigner,
  workflowSetSignerConditionRule,
  workflowSortSignPhases,
  workflowSetSignPhaseName,
  workflowSetStageConditionRule,
  workflowToggleSkipStage,
}) {
  const intl = useIntl();

  if (!signPhases || signPhases.length === 0) {
    // add an initial sign phase if the workflow doesn't have one
    workflowAddSigningPhase();
  }

  const checkPhaseTitle = (signPhase) => {
    signPhase.title = intl.formatMessage({
      id: 'Sign.recipientsTitle',
      defaultMessage: 'Recipients',
      description: 'Title for recipients phase',
    });
    return signPhase;
  };

  const signItems = signPhases.map((phase) => ({
    ...checkPhaseTitle(phase),
    content: (
      <FlexLayout disabled={disabled} flexDirection="column" space={8}>
        <FlexLayout flexDirection="column" space={4}>
          {phase.signers.map((signer, index) => (
            <Signer
              key={index}
              signer={signer}
              onRemoveSigner={() => {
                workflowRemoveSigner({ index, phaseId: phase.id });
              }}
              onUpdateSigner={(signerId) => {
                workflowSetSigner({ signerId, index, phaseId: phase.id });
              }}
              onUpdateSignerUser={(defaultSigner) => {
                workflowSetSigner({
                  signerId: signer.id,
                  index,
                  phaseId: phase.id,
                  defaultSigner,
                });
              }}
              onUpdateConditionRule={(conditionRule) => {
                workflowSetSignerConditionRule({
                  conditionRule,
                  index,
                  phaseId: phase.id,
                });
              }}
            />
          ))}
          <Box>
            <Button
              text={intl.formatMessage({
                id: 'Sign.addSignerButton',
                defaultMessage: 'Add Signer',
                description: 'Button text to add a new signer',
              })}
              onClick={() => workflowAddSigner(phase.id)}
            />
          </Box>
        </FlexLayout>
      </FlexLayout>
    ),
  }));

  const unspecifiedSignerIdsCount = errors.unspecifiedSignerIds.length;

  const handleUpdateStageConditionRule = (updatedConditionRule) => {
    workflowSetStageConditionRule({
      conditionRule: updatedConditionRule,
      stage: TicketStageType.Sign,
    });
  };

  const handleToggleSkipStage = (updatedIsSkipped) => {
    workflowToggleSkipStage({
      isSkipped: updatedIsSkipped,
      stage: TicketStageType.Sign,
    });
  };

  return (
    <FlexLayout disabled={disabled} flexDirection="column" space={6}>
      <SkipStageAccordion
        conditionRule={conditionRule}
        isSkipped={isSkipped}
        onChange={handleUpdateStageConditionRule}
        onToggle={handleToggleSkipStage}
      />
      <FlexLayout flexDirection="column" space={4}>
        <FormField
          description={intl.formatMessage({
            id: 'Sign.coordinatorDescription',
            defaultMessage:
              'A signature coordinator will be in charge of making sure that all the signatures have been collected.',
            description: 'Description for signing coordinator field',
          })}
          id="workflow--sign-coordinator"
          input={WorkflowUserRoleSingleSelect}
          label={intl.formatMessage({
            id: 'Sign.coordinatorLabel',
            defaultMessage: 'Signing Coordinator',
            description: 'Label for signing coordinator field',
          })}
          disabled={disabled}
          width="fullWidth"
          placeholder={intl.formatMessage({
            id: 'Sign.coordinatorPlaceholder',
            defaultMessage: 'Not specified',
            description: 'Placeholder for signing coordinator field',
          })}
          error={
            errors.unspecifiedCoordinator
              ? intl.formatMessage({
                  id: 'Sign.coordinatorRequiredError',
                  defaultMessage: 'You need to specify a signing coordinator.',
                  description:
                    'Error message when signing coordinator is not specified',
                })
              : undefined
          }
          value={coordinator}
          onChange={workflowSetSignCoordinator}
        />
      </FlexLayout>
      {unspecifiedSignerIdsCount > 0 && (
        <Text
          bg="red-100"
          color="red-600"
          p={4}
          sx={{ borderRadius: 'm' }}
          variant="xs-dense"
        >
          {intl.formatMessage(
            {
              id: 'Sign.unspecifiedSignersMessage',
              defaultMessage:
                'Please specify {count, plural, one {additional signer} other {# additional signers}} or remove the unassigned signers on this workflow via the sidebar.',
              description:
                'Message asking to specify additional signers. Example: count = 2',
            },
            { count: unspecifiedSignerIdsCount },
          )}
        </Text>
      )}
      <SignStageItems items={signItems} />
    </FlexLayout>
  );
}

function mapStateToProps({ builder, workflow }) {
  var stage = workflow.stages[TicketStageType.Sign];
  return {
    conditionRule: stage.conditionRule,
    coordinator: stage.coordinators[0] || null,
    disabled: !builder.shouldEnableAllComponents,
    errors: getSignErrors(workflow),
    isSkipped: !stage.isEnabled,
    signPhases: getSignPhases(workflow),
  };
}

export default connect(mapStateToProps, {
  workflowAddSigner,
  workflowAddSigningPhase,
  workflowRemoveSigner,
  workflowRemoveSigningPhase,
  workflowSetSignCoordinator,
  workflowSetSigner,
  workflowSetSignPhaseName,
  workflowSetSignerConditionRule,
  workflowSortSignPhases,
  workflowSetStageConditionRule,
  workflowToggleSkipStage,
})(Sign);
